// @flow
import * as React from 'react';
import { Box, Button, Flex, Tooltip } from '@locus-taxy/neo-ui';
import TeamChip from 'common/components/neo/overrides/TeamChip';
import { neoUIColors } from 'common/enums/ColorEnum';
import commonIds from 'common/tests/common/common.json';

type TeamType = {
    name: string,
    teamId: string,
    color?: string
};
type Props = {
    teams: Array<TeamType>,
    chipSize?: 'small' | 'medium',
    visibleCount?: number,
    onContextMenu?: (event: Event, id: string) => void,
    onClick?: (event: Event, id: string) => void,
    overflowPlacement?: 'left' | 'right' | 'bottom' | 'top',
    overflowButtonPalette?: 'neutral' | 'light'
};

const TeamChipWrapper = ({
    teamId,
    onContextMenu,
    onClick,
    color,
    name,
    chipSize
}) => {
    const _onContextMenu = React.useCallback(
        e => {
            onContextMenu?.(e, teamId);
        },
        [onContextMenu, teamId]
    );

    const _onClick = React.useCallback(
        e => {
            onClick?.(e, teamId);
        },
        [onClick, teamId]
    );

    return (
        <Box
            data-test-id={commonIds.teamsRow.teamChip}
            onContextMenu={_onContextMenu}
            onClick={_onClick}
            sx={{ cursor: onContextMenu || onClick ? 'pointer' : 'default' }}
        >
            <TeamChip color={color} teamLabel={name} size={chipSize} />
        </Box>
    );
};
const TeamsRow = ({
    teams,
    chipSize,
    onContextMenu,
    onClick,
    maxWidth = '100%'
}: {
    teams: Array<TeamType>,
    chipSize: 'small' | 'medium',
    onContextMenu?: (event: Event, id: string) => void,
    onClick?: (event: Event, id: string) => void,
    maxWidth?: number
}): React.Node => {
    return (
        <Flex maxWidth={maxWidth} flexWrap="wrap">
            {teams.map((team, index) => {
                const { name, color = neoUIColors.grey100.code } = team;
                // TODO: revisit the index as key
                return (
                    <Box
                        key={index}
                        my={1}
                        mr={index !== teams.length - 1 ? 1 : 0}
                    >
                        <TeamChipWrapper
                            teamId={team.teamId}
                            onContextMenu={onContextMenu}
                            onClick={onClick}
                            chipSize={chipSize}
                            color={color}
                            name={name}
                        />
                    </Box>
                );
            })}
        </Flex>
    );
};
const TeamsRowWithOverflow = ({
    teams,
    chipSize = 'small',
    visibleCount = 2,
    onContextMenu,
    onClick,
    overflowPlacement = 'right',
    overflowButtonPalette = 'neutral'
}: Props): React.Node => {
    const visibleTeams =
        teams.length > visibleCount ? teams.slice(0, visibleCount) : teams;
    const hiddenTeams =
        teams.length > visibleCount ? teams.slice(visibleCount) : null;

    return (
        <Flex alignItems="center">
            <TeamsRow
                teams={visibleTeams}
                chipSize={chipSize}
                onContextMenu={onContextMenu}
                onClick={onClick}
            />
            {hiddenTeams ? (
                <Tooltip
                    tooltip={
                        <Box maxHeight={300} overflow="auto">
                            <TeamsRow
                                teams={hiddenTeams}
                                chipSize={chipSize}
                                onContextMenu={onContextMenu}
                                onClick={onClick}
                                maxWidth={200}
                            />
                        </Box>
                    }
                    placement={overflowPlacement}
                    trigger="click"
                >
                    <Button
                        data-test-id={commonIds.teamsRow.moreButton}
                        ml={2}
                        appearance="icon"
                        icon="overflow_horizontal"
                        palette={overflowButtonPalette}
                    />
                </Tooltip>
            ) : null}
        </Flex>
    );
};

export default TeamsRowWithOverflow;
export { TeamsRowWithOverflow as TeamsRow };
